import {
  type captureEvent,
  makeBrowserOfflineTransport,
  makeFetchTransport,
} from "@sentry/react";

type TransportOptions = Parameters<typeof makeFetchTransport>[0];
type Transport = ReturnType<typeof makeFetchTransport>;
type Envelope = Parameters<Transport["send"]>[0];
type EnvelopeItem = Envelope[1][number];
type SentryEvent = Parameters<typeof captureEvent>[0];
// Sentry types the item header of events, transactions, profiles and feedback
// as one union; `isEventItem` narrows it at runtime to errors.
type EventItem = Extract<
  EnvelopeItem,
  [{ type: "event" | "transaction" | "profile" | "feedback" }, SentryEvent]
>;
// The envelope kinds that may carry an event item: those that still
// type-check with their items narrowed to event items only.
type EnvelopeCarrying<Item, Kind = Envelope> = Kind extends Envelope
  ? [Kind[0], Item[]] extends Kind
    ? Kind
    : never
  : never;
type EventEnvelope = EnvelopeCarrying<EventItem>;

// URLs inside free text. A URL ends at whitespace, a fragment, a double quote
// or an angle bracket, none of which it may contain unencoded; parentheses
// and single quotes are part of it. No lookbehind: Safari before 16.4 cannot
// parse it, and the build does not transpile regular expressions.
const ABSOLUTE_URL_WITH_QUERY =
  /([a-z][a-z\d+.-]*:\/\/[^\s?#"<>]*)\?[^\s#"<>]*/gi;
const ROOTED_PATH_WITH_QUERY = /(^|[^\w.:/])(\/[^\s?#"<>]*)\?[^\s#"<>]*/g;
// A query on a relative reference ("reset?token=…", "?token=…").
const KEY_VALUE_QUERY = /\?[^\s#"<>?=]*=[^\s#"<>]*/g;

function isEventItem(item: EnvelopeItem): item is EventItem {
  return item[0].type === "event";
}

function isEventEnvelope(envelope: Envelope): envelope is EventEnvelope {
  return envelope[1].some(isEventItem);
}

function withoutQueryStrings(text: string): string {
  return text
    .replace(ABSOLUTE_URL_WITH_QUERY, "$1")
    .replace(ROOTED_PATH_WITH_QUERY, "$1$2")
    .replace(KEY_VALUE_QUERY, "");
}

// A field that holds only a URL, possibly the raw argument an app passed to
// fetch: everything from its first "?" goes.
function withoutQuery(url: string): string {
  const query = url.indexOf("?");
  return query === -1 ? url : url.slice(0, query);
}

const URL_BREADCRUMB_KEYS = ["url", "from", "to"];

// URLs embedded in free text, such as the React component stack or a message.
function stripEmbeddedQueryStrings(value: unknown): unknown {
  if (typeof value === "string") return withoutQueryStrings(value);
  if (Array.isArray(value)) {
    value.forEach((entry, index) => {
      value[index] = stripEmbeddedQueryStrings(entry);
    });
  } else if (value && typeof value === "object") {
    for (const [key, entry] of Object.entries(value)) {
      Reflect.set(value, key, stripEmbeddedQueryStrings(entry));
    }
  }
  return value;
}

// Strips the query string from every URL the SDK fills in an event: whole in
// the fields that hold only a URL, and by pattern in all other text.
function stripQueryStrings(event: SentryEvent): void {
  const { request, breadcrumbs, exception, debug_meta } = event;
  if (request) {
    if (request.url) request.url = withoutQuery(request.url);
    delete request.query_string;
    const headers = request.headers ?? {};
    for (const [name, value] of Object.entries(headers)) {
      if (name.toLowerCase() === "referer") headers[name] = withoutQuery(value);
    }
  }
  for (const { data } of breadcrumbs ?? []) {
    for (const key of URL_BREADCRUMB_KEYS) {
      const value = data?.[key];
      if (data && typeof value === "string") data[key] = withoutQuery(value);
    }
  }
  for (const value of exception?.values ?? []) {
    for (const frame of value.stacktrace?.frames ?? []) {
      if (frame.filename) frame.filename = withoutQuery(frame.filename);
      if (frame.abs_path) frame.abs_path = withoutQuery(frame.abs_path);
    }
  }
  for (const image of debug_meta?.images ?? []) {
    if ("code_file" in image && image.code_file) {
      image.code_file = withoutQuery(image.code_file);
    }
  }
  stripEmbeddedQueryStrings(event);
}

/**
 * Sentry transport for the web app. Error events go through an IndexedDB
 * queue, so an error captured offline is sent once the device is back online
 * (on the `online` event, by the queue's own retry, or on the next start).
 * Query strings are stripped from every URL in the event, and from the
 * transaction name in any envelope's trace header, before it can be stored or
 * sent, because links such as password resets carry tokens.
 *
 * Everything else (replays, sessions, transactions, logs, and any attachment
 * sent with an error) goes straight to the network and is lost offline, so it
 * can never fill the queue and push an error out. Both paths share one fetch
 * transport, so a rate limit Sentry sets on either applies to both. The queue
 * is best effort: if IndexedDB is unavailable or full, the event is dropped
 * and capture still works online.
 */
export function makeAppTransport(options: TransportOptions): Transport {
  const network = makeFetchTransport(options);
  const queued = makeBrowserOfflineTransport(() => network)({
    ...options,
    flushAtStartup: true,
  });

  return {
    send: async (envelope) => {
      // The trace header names the transaction, which can carry a query.
      const trace: unknown = Reflect.get(envelope[0], "trace");
      if (
        trace &&
        typeof trace === "object" &&
        "transaction" in trace &&
        typeof trace.transaction === "string"
      ) {
        trace.transaction = withoutQuery(trace.transaction);
      }
      if (!isEventEnvelope(envelope)) return network.send(envelope);

      const [headers, items] = envelope;
      const events = items.filter(isEventItem);
      const rest = items.filter((item) => !isEventItem(item));
      for (const [, event] of events) stripQueryStrings(event);

      const [result] = await Promise.all([
        queued.send([headers, events]),
        rest.length > 0 ? network.send([headers, rest]) : undefined,
      ]);
      return result;
    },
    flush: (timeout) => queued.flush(timeout),
  };
}
